import { useState, forwardRef, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
// material
import { styled } from '@mui/material/styles';
import {
  Box,
  Grid,
  Typography,
  Stack,
  Modal,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Button,
  TextField,
  Slide,
  Autocomplete,
  createFilterOptions,
  FormHelperText
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import jsPDF from 'jspdf';
import * as Yup from 'yup';
import { useFormik, Form, FormikProvider } from 'formik';
import {
  extractHierarchyInfoFromPath,
  getAdminRoleFromHierarchy,
  parseValidationErrorMessage,
  parseValidationErrorMessageForBulkUpload,
  removeNulls
} from '../../../utils/Utilities';
import { useAuth } from '../../customContextProviders/AuthContext';
import { AlertDialog, SnackBarAlert } from '../../HelperComponents';
import {
  MerchantBulkUpload,
  AddNewMerchant,
  UpdateMerchant,
  GetAllMerchants
} from '../../../network/Merchants';
import { APP_CONSTANTS, indianStates } from '../../../URLConstants';
import mccData from '../../../../public/static/mcc.json';
import { ConfirmDialog } from '../app';

/* eslint-disable */
const SlideTransition = forwardRef(function Transition(props, ref) {
  return <Slide direction="down" ref={ref} {...props} />;
});
/* eslint-enable */

const VisuallyHiddenInput = styled('input')({
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  height: 1,
  overflow: 'hidden',
  position: 'absolute',
  bottom: 0,
  left: 0,
  whiteSpace: 'nowrap',
  width: 1
});

const style = {
  position: 'absolute',
  width: '100%',
  maxWidth: { sm: '90%', xs: '95%', md: 600 },
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  minWidth: '70%',
  bgcolor: 'background.paper',
  border: '1px solid #000',
  boxShadow: 24,
  p: 4,
  borderRadius: 1
};

const DeviceSchema = Yup.object().shape({
  merchantAddress: Yup.string()
    .required('Required')
    .test('not-only-whitespace', 'Field should not contain only whitespace', (value) =>
      /\S/.test(value)
    ),

  merchantCity: Yup.string()
    .required('Required')
    .test('not-only-whitespace', 'Field should not contain only whitespace', (value) =>
      /\S/.test(value)
    ),

  merchantId: Yup.string()
    .required('Required')
    .test('no-space', 'Merchant Id should not contain spaces', (value) => !/\s/.test(value)),

  merchantVpa: Yup.string()
    .required('Required')
    .test('no-space', 'Merchant VPA should not contain spaces', (value) => !/\s/.test(value)),

  merchantMobileNo: Yup.string()
    .required('Phone number is required')
    .matches(/^[6-9]\d{9}$/, 'Invalid phone number, 10 Digits required.'),

  merchantName: Yup.string()
    .required('Required')
    .test('not-only-whitespace', 'Field should not contain only whitespace', (value) =>
      /\S/.test(value)
    )
    .test(
      'no-double-space',
      'Double spaces are not allowed',
      (value) => !value || !/\s{2}/.test(value)
    )
    .matches(/^[\p{L}\p{N}\s]+$/u, 'Special characters are not allowed'),

  merchantPincode: Yup.string()
    .required('Required')
    .matches(/^[0-9]{6}$/, 'Pincode must be exactly 6 digits'),

  merchantState: Yup.string()
    .required('Required')
    .test('not-only-whitespace', 'Field should not contain only whitespace', (value) =>
      /\S/.test(value)
    ),
  deviceLanguage: Yup.string().required('Required'),

  merchantBranch: Yup.string().required('Required'),

  merchantBranchId: Yup.string().required('Required'),

  // regionalOfficeName: Yup.string().required('Required')
});

BomDeviceRequestFormModal.propTypes = {
  merchantData: PropTypes.object,
  opneAssignedModal: PropTypes.bool,
  handleClose: PropTypes.func,
  _onAction: PropTypes.func,
  accountDetails: PropTypes.object,
  branchesList: PropTypes.object
};

export default function BomDeviceRequestFormModal({
  merchantData,
  opneAssignedModal,
  handleClose,
  _onAction,
  accountDetails,
  branchesList,
  fetchBranchesList,
  regionsList
}) {
  const { sessionUserData, allBOIBranchesRegion } = useAuth();
  const fileInputRef = useRef(null);

  const isMakerOrChecker =
    sessionUserData?.user?.role === APP_CONSTANTS?.USER_ROLE_MAKER ||
    sessionUserData?.user?.role === APP_CONSTANTS?.USER_ROLE_CHECKER;

  const [error, setError] = useState({
    accountNo: false,
    phone: false
  });

  const adminRole = getAdminRoleFromHierarchy(accountDetails?.path);
  const isHOAdmin = adminRole === 'HOAdmin';

  const [openAlertDialog, setOpenAlertDialog] = useState(false);
  const [alertDialogMetas, setAlertDialogMetas] = useState({
    message: '',
    title: '',
    btnLabel: '',
    bulkUploadError: null
  });

  const [isSnackBarAlertOpen, setIsSnackBarAlertOpen] = useState(false);
  const [snackBarMetas, setSnackBarMetas] = useState({
    title: '',
    message: '',
    severity: 'info',
    handleClose: null
  });

  const [openConfirm, setOpenConfirm] = useState(false);
  const [dialogConfig, setDialogConfig] = useState({
    loading: false,
    message: '',
    onConfirm: () => {},
    onCancel: () => setOpenConfirm(false),
    confirmText: ''
  });

  const addBomDeviceFormik = useFormik({
    enableReinitialize: true,
    initialValues: {
      merchantAddress: merchantData?.address || '',
      merchantCity: merchantData?.city || '',
      merchantId: '',
      merchantMobileNo: merchantData?.phone || '',
      merchantName: merchantData?.name || '',
      merchantPincode: merchantData?.pincode || '',
      merchantState: merchantData?.region || merchantData?.state || '',
      deviceLanguage: merchantData?.language?.toLowerCase() || '',
      merchantBranch: '',
      merchantBranchId: merchantData?.branchId || '',
      merchantVpa: '',
      uniqueRef: merchantData?.details?.uniqueRef || '',
      mcc: Number(merchantData?.details?.mcc) || '',
      // regionalOfficeName: '',
      // regionalOfficeId: ''
    },
    validationSchema: DeviceSchema,
    onSubmit: () => {
      const formatMcc = (mcc) => {
        if (!mcc) return mcc; // if it's null/undefined, just return it
        const mccStr = String(mcc);
        return mccStr.length === 3 ? `0${mccStr}` : mccStr;
      };

      const payload = removeNulls({
        name: addBomDeviceFormik?.values?.merchantName,
        phone: addBomDeviceFormik?.values?.merchantMobileNo,
        branch: addBomDeviceFormik?.values?.merchantBranch,
        branchId: addBomDeviceFormik?.values?.merchantBranchId.toUpperCase(),
        // region: addBomDeviceFormik?.values?.regionalOfficeName,
        address: addBomDeviceFormik?.values?.merchantAddress,
        state: addBomDeviceFormik?.values?.merchantState,
        city: addBomDeviceFormik?.values?.merchantCity,
        pincode: addBomDeviceFormik?.values?.merchantPincode,
        mid: addBomDeviceFormik?.values?.merchantId,
        vpa: addBomDeviceFormik?.values?.merchantVpa,
        language: addBomDeviceFormik?.values?.deviceLanguage,
        details: {
          uniqueRef: 'PRX',
          mcc: formatMcc(addBomDeviceFormik?.values?.mcc)
        }
      });

      const apiCall = () => {
        const call = merchantData
          ? UpdateMerchant(merchantData?.id, payload)
          : AddNewMerchant(payload);

        call
          .then((_res) => {
            addBomDeviceFormik.setSubmitting(false);
            handleClose();
            setSnackBarMetas((prevState) => ({
              ...prevState,
              title: merchantData
                ? 'Merchant has been successfully updated'
                : 'Merchant has been successfully on-boarded',
              severity: 'success'
            }));
            setIsSnackBarAlertOpen(true);
            addBomDeviceFormik.resetForm();
          })
          .catch((error) => {
            addBomDeviceFormik.setSubmitting(false);
            setAlertDialogMetas({
              ...alertDialogMetas,
              message: parseValidationErrorMessage(error),
              title: 'Failed to process your request',
              btnLabel: 'OK'
            });
            setOpenAlertDialog(true);
          });
      };

      if (error?.accountNo || error?.phone) {
        setDialogConfig({
          loading: false,
          message: `Merchant already exists with phone number - ${values?.merchantMobileNo}. Do you still want to proceed?`,
          onConfirm: () => {
            setOpenConfirm(false);
            apiCall();
          },
          onCancel: () => {
            setOpenConfirm(false);
            addBomDeviceFormik?.setSubmitting(false);
          },
          confirmText: 'Yes',
          cancelText: 'No'
        });

        setOpenConfirm(true);
      } else {
        apiCall();
      }
    }
  });

  useEffect(() => {
    if (!opneAssignedModal) return;

    const hierarchyRegionalOffice = accountDetails?.path
      ? extractHierarchyInfoFromPath(accountDetails?.path)?.regionalOffice
      : null;
    const regionalOfficeId = hierarchyRegionalOffice
      ? regionsList?.find((region) => region.name === hierarchyRegionalOffice)?.id
      : undefined;
    fetchBranchesList(regionalOfficeId);

    if (isMakerOrChecker && !merchantData && accountDetails?.path) {
      setFieldValue(
        'merchantBranchId',
        extractHierarchyInfoFromPath(accountDetails?.path)?.branchId
      );
      // setFieldValue(
      //   'regionalOfficeName',
      //   extractHierarchyInfoFromPath(accountDetails?.path)?.regionalOffice
      // );
    }
  }, [accountDetails, opneAssignedModal, regionsList]);

  const {
    errors,
    touched,
    values,
    isSubmitting,
    handleSubmit,
    handleBlur,
    handleChange,
    getFieldProps,
    resetForm,
    setFieldValue
  } = addBomDeviceFormik;

  const handleFileUpload = (event) => {
    addBomDeviceFormik.setSubmitting(true);
    MerchantBulkUpload(event.target.files[0])
      .then((_res) => {
        addBomDeviceFormik.setSubmitting(false);

        if (fileInputRef.current) {
          fileInputRef.current.value = null;
        }
        setSnackBarMetas((prevState) => ({
          ...prevState,
          title: 'Merchants have been successfully created !',
          severity: 'success'
        }));
        setIsSnackBarAlertOpen(true);

        handleClose();
      })
      .catch((error) => {
        addBomDeviceFormik.setSubmitting(false);

        if (fileInputRef.current) {
          fileInputRef.current.value = null;
        }
        setAlertDialogMetas({
          ...alertDialogMetas,
          message: parseValidationErrorMessageForBulkUpload(error),
          title: 'Error processing the file',
          btnLabel: 'OK',
          bulkUploadError: parseValidationErrorMessageForBulkUpload(error)
        });
        setOpenAlertDialog(true);
      });
  };

  function downloadBulkUploadSampleFile() {
    const link = document.createElement('a');
    link.href = process.env?.REACT_APP_B2B_CLIENT_BULK_UPLOAD_SAMPLE_FILE;
    link.download = 'merchant_onboarding_file.csv';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  function exportErrorReport() {
    // eslint-disable-next-line new-cap
    const doc = new jsPDF();
    const logoUrl = 'https://i.postimg.cc/FKLr13w7/logo.png';
    const img = new Image();
    img.src = logoUrl;
    img.onload = function () {
      const now = new Date();
      const formattedDate = now.toLocaleDateString();
      const formattedTime = now.toLocaleTimeString();

      doc.setFont('Helvetica', 'bold');
      doc.text(10, 11, `Error Report`);

      doc.setFont('Mulish', 'normal');
      doc.text(10, 20, `${formattedDate} ${formattedTime}`);

      const errorMessage =
        typeof alertDialogMetas?.bulkUploadError === 'string'
          ? alertDialogMetas?.bulkUploadError
          : JSON.stringify(alertDialogMetas?.bulkUploadError, null, 2);

      const maxWidth = 190; // Adjust this value as needed
      const textX = 10;
      const textY = 30;

      doc.text(errorMessage || 'NA', textX, textY, { maxWidth });

      doc.save(`error_report_${formattedDate}_${formattedTime}.pdf`);
      setOpenAlertDialog(false);
    };
  }

  const filter = createFilterOptions();

  const handlePhoneChange = async (e) => {
    const { value } = e.target;
    addBomDeviceFormik.setFieldValue('merchantMobileNo', value);

    setError((prev) => ({
      ...prev,
      phone: false
    }));

    if (value.length === 10) {
      try {
        const res = await GetAllMerchants(1, 1, { phone: value });

        if (res?.data?.data && res.data.data.length > 0) {
          setError((prev) => ({
            ...prev,
            phone: true
          }));
        }
      } catch (err) {
        console.error('API Error', err);
      }
    }
  };

  return (
    <Box>
      <Modal
        open={opneAssignedModal}
        onClose={() => {
          handleClose();
          addBomDeviceFormik?.resetForm();
        }}
        components={{ Transition: SlideTransition }}
        aria-labelledby="device-request-modal-title"
        aria-describedby="device-request-modal-description"
      >
        <FormikProvider value={addBomDeviceFormik}>
          <Form autoComplete="off" noValidate onSubmit={handleSubmit}>
            <Box
              sx={{
                ...style,
                maxHeight: '90vh', // 90% of the viewport height
                width: '100%',
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column',
                overflowY: 'auto'
              }}
            >
              <Typography sx={{ fontSize: 18, fontWeight: 700, fontFamily: 'Montserrat' }}>
                New Assign Request
              </Typography>
              <Grid container spacing={2} sx={{ mt: 1 }}>
                {/* Region field removed from form (validation / payload / hierarchy) — restore block below if needed.
                {isMakerOrChecker ? (
                  <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                    <FormControl fullWidth>
                      <TextField
                        {...getFieldProps('regionalOfficeName')}
                        error={Boolean(touched.regionalOfficeName && errors.regionalOfficeName)}
                        helperText={touched.regionalOfficeName && errors.regionalOfficeName}
                        multiline
                        maxRows={4}
                        label="Region *"
                        disabled={isMakerOrChecker}
                        InputLabelProps={{ shrink: isMakerOrChecker }}
                      />
                    </FormControl>
                  </Grid>
                ) : (
                  <Grid size={{ xs: 12, sm: 12, md: 4 }}>
                    <Autocomplete
                      options={regionsList || []}
                      getOptionLabel={(option) => option.name || ''}
                      value={regionsList?.find((r) => r.id === values.regionalOfficeId) || null}
                      onChange={(e, newValue) => {
                        const selectedId = newValue ? newValue.id : '';
                        setFieldValue('regionalOfficeId', selectedId);
                        setFieldValue('regionalOfficeName', newValue ? newValue.name : '');

                        if (selectedId) {
                          fetchBranchesList(selectedId);
                        }
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Region *"
                          error={Boolean(touched.regionalOfficeId && errors.regionalOfficeId)}
                          helperText={touched.regionalOfficeId && errors.regionalOfficeId}
                        />
                      )}
                      isOptionEqualToValue={(option, value) => option.id === value.id}
                    />
                  </Grid>
                )}
                */}

                <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                  <FormControl fullWidth>
                    <Autocomplete
                      disablePortal
                      freeSolo
                      id="nbg-branch-id"
                      options={allBOIBranchesRegion?.branches || []}
                      onChange={(_event, newValue) => {
                        if (newValue !== '' && newValue !== undefined && newValue !== null)
                          handleChange('merchantBranch')(newValue);
                        else handleChange('merchantBranch')('');
                      }}
                      onBlur={handleBlur('merchantBranch')}
                      value={addBomDeviceFormik?.values?.merchantBranch}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Branch *"
                          {...getFieldProps('merchantBranch')}
                          error={Boolean(touched.merchantBranch && errors.merchantBranch)}
                          helperText={touched.merchantBranch && errors.merchantBranch}
                        />
                      )}
                      renderOption={(props, option) => (
                        <MenuItem {...props} key={option} value={option} sx={{ p: 1.5 }}>
                          {' '}
                          {option}{' '}
                        </MenuItem>
                      )}
                    />
                  </FormControl>
                </Grid>

                {isMakerOrChecker ? (
                  <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                    <FormControl fullWidth>
                      <TextField
                        {...getFieldProps('merchantBranchId')}
                        error={Boolean(touched.merchantBranchId && errors.merchantBranchId)}
                        helperText={touched.merchantBranchId && errors.merchantBranchId}
                        multiline
                        maxRows={4}
                        label="Branch Id *"
                        disabled={isMakerOrChecker}
                        InputLabelProps={{ shrink: isMakerOrChecker }}
                      />
                    </FormControl>
                  </Grid>
                ) : (
                  <Grid size={{ xs: 12, sm: 12, md: 4 }}>
                    <Autocomplete
                      options={branchesList || []}
                      getOptionLabel={(option) => option.branchId || ''}
                      value={
                        branchesList?.find((b) => b.branchId === values.merchantBranchId) || null
                      }
                      onChange={(e, newValue) => {
                        setFieldValue('merchantBranchId', newValue ? newValue.branchId : '');
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Branch Id *"
                          error={Boolean(touched.merchantBranchId && errors.merchantBranchId)}
                          helperText={touched.merchantBranchId && errors.merchantBranchId}
                        />
                      )}
                      isOptionEqualToValue={(option, value) => option.branchId === value?.branchId}
                    />
                  </Grid>
                )}

                <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                  <FormControl fullWidth>
                    <TextField
                      {...getFieldProps('merchantId')}
                      error={Boolean(touched.merchantId && errors.merchantId)}
                      helperText={touched.merchantId && errors.merchantId}
                      label="Merchant Id *"
                    />
                  </FormControl>
                </Grid>

                <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                  <FormControl fullWidth>
                    <TextField
                      {...getFieldProps('merchantCity')}
                      error={Boolean(touched.merchantCity && errors.merchantCity)}
                      helperText={touched.merchantCity && errors.merchantCity}
                      label="Merchant City *"
                    />
                  </FormControl>
                </Grid>

                <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                  <FormControl fullWidth>
                    <TextField
                      {...getFieldProps('merchantVpa')}
                      error={Boolean(touched.merchantVpa && errors.merchantVpa)}
                      helperText={touched.merchantVpa && errors.merchantVpa}
                      multiline
                      maxRows={4}
                      label="Merchant VPA"
                    />
                  </FormControl>
                </Grid>

                <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                  <Autocomplete
                    freeSolo
                    options={mccData}
                    getOptionLabel={(option) => {
                      if (typeof option === 'string') return option;
                      if (option.inputValue) return option.inputValue;

                      if (
                        option.MCCName &&
                        option.MCCCode &&
                        option.MCCName.includes(`(${option.MCCCode})`)
                      ) {
                        return option.MCCName;
                      }

                      return `${option.MCCName} (${option.MCCCode})`;
                    }}
                    filterOptions={(options, params) => {
                      const filtered = filter(options, params);
                      const { inputValue } = params;
                      const isExisting = options.some((opt) => opt.MCCCode === inputValue);

                      if (inputValue !== '' && !isExisting) {
                        filtered.push({
                          inputValue,
                          MCCName: `Add "${inputValue}"`,
                          MCCCode: inputValue
                        });
                      }

                      return filtered;
                    }}
                    value={
                      mccData.find((opt) => opt.MCCCode === values.mcc) ||
                      (values.mcc ? values.mcc : null)
                    }
                    onChange={(event, newValue) => {
                      const extractCode = (val) => {
                        if (!val) return '';

                        const match = val.match(/\((\d+)\)/);
                        if (match) return match[1];

                        if (/^\d+$/.test(val)) return val;

                        return val;
                      };

                      let code = '';

                      if (typeof newValue === 'string') {
                        code = extractCode(newValue);
                      } else if (newValue?.inputValue) {
                        code = extractCode(newValue.inputValue);
                      } else {
                        code = newValue?.MCCCode || '';
                      }

                      // valid if:
                      // - exactly 3 digits
                      // - OR exactly 4 digits starting with 0 (example: 0180)
                      const isValidMcc = /^\d{3}$/.test(code) || /^0\d{3}$/.test(code);

                      if (isValidMcc) {
                        setFieldValue('mcc', code);
                      }
                    }}
                    isOptionEqualToValue={(option, value) => {
                      if (typeof value === 'string') return option.MCCCode === value;
                      return option.MCCCode === value?.MCCCode;
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Merchant Category Code *"
                        error={Boolean(touched.mcc && errors.mcc)}
                        helperText={touched.mcc && errors.mcc}
                        inputProps={{
                          ...params.inputProps,
                          'aria-label': 'Merchant Category Code *'
                        }}
                      />
                    )}
                  />
                </Grid>

                <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                  <FormControl fullWidth>
                    <TextField
                      {...getFieldProps('uniqueRef')}
                      error={Boolean(touched.uniqueRef && errors.uniqueRef)}
                      helperText={touched.uniqueRef && errors.uniqueRef}
                      multiline
                      maxRows={4}
                      label="Account No *"
                    />
                  </FormControl>
                </Grid>

                <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                  <FormControl fullWidth>
                    <TextField
                      {...getFieldProps('merchantName')}
                      error={Boolean(touched.merchantName && errors.merchantName)}
                      helperText={touched.merchantName && errors.merchantName}
                      label="Merchant Name *"
                    />
                  </FormControl>
                </Grid>

                <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                  <Autocomplete
                    freeSolo={false} // disables typing values not in the list
                    options={indianStates}
                    value={values?.merchantState}
                    onChange={(event, newValue) => setFieldValue('merchantState', newValue)}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Select State *"
                        placeholder="Type to search..."
                        error={Boolean(touched.mcc && errors.mcc)}
                        helperText={touched.mcc && errors.mcc}
                      />
                    )}
                  />
                </Grid>

                <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                  <FormControl fullWidth>
                    <InputLabel id="device-language-request-label">Language *</InputLabel>
                    <Select
                      labelId="device-language-request-label"
                      id="device-language-request-select"
                      label="Device Language"
                      {...getFieldProps('deviceLanguage')}
                      error={Boolean(touched.deviceLanguage && errors.deviceLanguage)}
                      helperText={touched.deviceLanguage && errors.deviceLanguage}
                    >
                      <MenuItem value="hi" sx={{ p: 1.5 }}>
                        Hindi
                      </MenuItem>
                      <MenuItem value="en" sx={{ p: 1.5 }}>
                        English
                      </MenuItem>
                      <MenuItem value="mr" sx={{ p: 1.5 }}>
                        Marathi
                      </MenuItem>
                      <MenuItem value="bn" sx={{ p: 1.5 }}>
                        Bengali
                      </MenuItem>
                      <MenuItem value="gj" sx={{ p: 1.5 }}>
                        Gujarati
                      </MenuItem>
                      <MenuItem value="pu" sx={{ p: 1.5 }}>
                        Punjabi
                      </MenuItem>
                    </Select>
                    {touched.deviceLanguage && errors.deviceLanguage && (
                      <FormHelperText error>{errors.deviceLanguage}</FormHelperText>
                    )}
                  </FormControl>
                </Grid>

                <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                  <FormControl fullWidth>
                    <TextField
                      {...getFieldProps('merchantMobileNo')}
                      error={Boolean(touched.merchantMobileNo && errors.merchantMobileNo)}
                      helperText={touched.merchantMobileNo && errors.merchantMobileNo}
                      label="Merchant Mobile No *"
                      onChange={handlePhoneChange}
                    />
                  </FormControl>
                </Grid>

                <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                  <FormControl fullWidth>
                    <TextField
                      {...getFieldProps('merchantPincode')}
                      error={Boolean(touched.merchantPincode && errors.merchantPincode)}
                      helperText={touched.merchantPincode && errors.merchantPincode}
                      label="Merchant Pincode *"
                    />
                  </FormControl>
                </Grid>

                <Grid size={{ xs: 12, sm: 12, md: 12 }}>
                  <FormControl fullWidth>
                    <TextField
                      {...getFieldProps('merchantAddress')}
                      error={Boolean(touched.merchantAddress && errors.merchantAddress)}
                      helperText={touched.merchantAddress && errors.merchantAddress}
                      multiline
                      rows={2}
                      maxRows={4}
                      label="Merchant Address *"
                    />
                  </FormControl>
                </Grid>
              </Grid>

              <Stack
                direction="row"
                justifyContent={merchantData ? 'flex-end' : 'space-between'}
                alignItems="center"
                spacing={1}
                sx={{ mt: 3, width: '100%' }}
              >
                {!merchantData ? (
                  <Stack
                    direction="row"
                    justifyContent="flex-start"
                    alignItems="center"
                    spacing={3}
                    sx={{ mt: 2 }}
                  >
                    <LoadingButton
                      loading={addBomDeviceFormik?.isSubmitting}
                      component="label"
                      variant="contained"
                      startIcon={<CloudUploadIcon />}
                    >
                      Bulk Upload
                      <VisuallyHiddenInput
                        ref={fileInputRef}
                        onChange={handleFileUpload}
                        type="file"
                        accept=".csv"
                      />
                    </LoadingButton>

                    <Box sx={{ display: 'flex' }}>
                      <LoadingButton
                        variant="text"
                        onClick={() => downloadBulkUploadSampleFile()}
                        sx={{
                          fontSize: 14,
                          fontWeight: 800,
                          color: 'blue',
                          textDecoration: 'underline',
                          cursor: 'pointer'
                        }}
                      >
                        Download bulk upload sample file{' '}
                      </LoadingButton>
                    </Box>

                    {/* <Box sx={{ display: 'flex' }}>
                    <Typography sx={{ fontSize: 14, fontWeight: 800, color: 'black' }}>Device in Production : </Typography>
                    <Typography sx={{ fontSize: 14, fontWeight: 800, color: 'red', pl: 1 }}>---</Typography>
                  </Box>  */}
                  </Stack>
                ) : null}
                <Stack
                  direction="row"
                  justifyContent="flex-end"
                  alignItems="center"
                  spacing={1}
                  sx={{ mt: 3 }}
                >
                  <Button
                    variant="contained"
                    color="error"
                    onClick={() => {
                      handleClose();
                      addBomDeviceFormik?.resetForm();
                    }}
                  >
                    Cancel
                  </Button>
                  <LoadingButton
                    loading={addBomDeviceFormik.isSubmitting}
                    variant="contained"
                    color="info"
                    type="submit"
                  >
                    {merchantData ? 'Update' : 'Save'}
                  </LoadingButton>
                </Stack>
              </Stack>
            </Box>
          </Form>
        </FormikProvider>
      </Modal>

      <AlertDialog
        openAlertDialog={openAlertDialog}
        message={alertDialogMetas?.message}
        title={alertDialogMetas?.title}
        btnLabel={alertDialogMetas?.btnLabel}
        handleClose={() => setOpenAlertDialog(false)}
        bulkUploadError={alertDialogMetas?.bulkUploadError}
        exportErrorReport={() => exportErrorReport()}
      />
      <ConfirmDialog
        open={openConfirm}
        message={dialogConfig.message}
        onCancel={dialogConfig.onCancel}
        onConfirm={dialogConfig.onConfirm}
        loading={dialogConfig?.loading}
        note={dialogConfig?.note}
        confirmText={dialogConfig?.confirmText}
        cancelText={dialogConfig?.cancelText}
      />

      <SnackBarAlert
        open={isSnackBarAlertOpen}
        title={snackBarMetas?.title}
        message={snackBarMetas?.message}
        severity={snackBarMetas?.severity}
        handleClose={() => setIsSnackBarAlertOpen(false)}
      />
    </Box>
  );
}
